<script lang="ts">
	import { get } from 'svelte/store';
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system
	import { Alert, Container, IconButton, Text, Button } from '@Shelly/shelly-design-system';
	//Helpers
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import { location, replace } from 'svelte-spa-router';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { retrieveVCGroups } from 'src/helpers/virtual_helpers/virtual_group_helpers';
	import {
		getDeviceIDByComponent,
		virtualBthomeTranslationValues,
	} from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { getVirtualTemplate } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_helpers';
	//Components
	import VirtualComponent from './VirtualComponent.svelte';
	import CreateVCGroup from '../Settings/CreateVCGroup.svelte';
	import BthomeButtonControl from './BthomeButtonControl.svelte';
	import VGroupModalSettings from '../Settings/VGroupModalSettings.svelte';
	import CreateTemplateModal from '../Templates/wizard/CreateTemplateModal.svelte';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	//Types
	import type { ComponentsGroupInterface } from 'src/types/VirtualComponents';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import VirtualComponentV2 from '../ComponentsV2/VirtualComponentV2.svelte';
	import BthomeButtonControlV2 from '../ComponentsV2/BthomeButtonControlV2.svelte';

	$: settingsStore = getDeviceSettings(device.id);
	export let device: Device;
	export let showCreateButton = false;

	const _device: Device = device;
	const deviceID = real_id(device.id);

	const init = (device: Device) => {
		const status: DeviceStatusG2 = devices_status.getByRealId(deviceID);
		const setting: DeviceSettingsG2 = devices_settings.getByRealId(deviceID);
		if (setting && status) {
			const vcGroups = retrieveVCGroups(device, status, setting);
			if (vcGroups.components.toString() !== components.toString()) {
				components = vcGroups.components;
				groups = vcGroups.groups;
				bthomeSensorInGroups = /(bthomesensor|bthomedevice):2\d{2}/.test(components.toString());
			}
		}
	};

	let groups: ComponentsGroupInterface[] = [];
	let components: string[] = [];
	let selectedGroup: string;
	let bthomeSensorInGroups = false;
	let showVCModalGroupSettings = false;
	let showCreateTemplateModal = false;
	let showVCModalCreate = false;

	$: init(device);
</script>

{#if groups.length === 0}
	<Alert
		type="info"
		class="mb-s"
		heading={$format('no_groups_title', { default: 'There are no groups yet' })}
	/>
{:else}
	{#if !device.shared && bthomeSensorInGroups && $settingsStore?.ble?.enable === false}
		<Alert
			type="warning"
			class="mb-s mt-s"
			heading={$format('alert_message_virtual_components_ble_disabled', {
				default: 'Bluetooth is disabled. Enable it in order to use BTHome Components feature.',
			})}
			cta={{
				title: $format('alert_title_virtual_components_go_to_blu_settings', {
					default: 'Go to Bluetooth settings',
				}),
			}}
			on:cta_click={() => navigateToSetting(real_id(device.id), 'networks', 'bluetooth')}
		/>
	{/if}
	{#each groups as group}
		<Container class="mb-s" padding="0">
			{#if device.channel < 100}
				{@const virtualDevice = getDeviceIDByComponent(deviceID, group.key)}
				<div class="d-flex justify-content-between align-items-center">
					<div class="ml-s d-flex flex-direction-column justify-content-between">
						<Text element="h2">
							{group.config?.name ||
								(/^bthomedevice/.test(group.key)
									? $format('text_virtual_bthome_device', {
											values: { id: group.config?.id },
											default: 'BTHome device ({id})',
										})
									: group.key)}</Text
						>
						{#if $devices?.[virtualDevice]}
							<Text
								class="link-styles text-small"
								on:click={() => {
									const vDevice = get(devices)?.[virtualDevice];
									if (!vDevice) return;
									if (
										vDevice?.room_id &&
										vDevice.room_id != device.room_id &&
										get(location)?.split('/')?.[3] != '-1'
									) {
										replace(`/home/room/${vDevice.room_id}/devices`);
									}
									setSidePanelState({ type: 'device', id: virtualDevice });
								}}
							>
								{$format('text_virtual_device_open_device', {
									default: 'extract device',
								})}
							</Text>
						{:else if /^bthomedevice/.test(group.key)}
							<Text class="text-secondary text-small">
								{group.config?.addr}</Text
							>
						{/if}
					</div>
					<IconButton
						aria-label={$format('button_show_modal_group_settings', {
							default: 'Show group settings',
						})}
						class="mb-s mt-s mr-s"
						icon="fas fa-gear"
						on:click={() => {
							selectedGroup = group.key;
							const vcDeviceID = getDeviceIDByComponent(device.id, selectedGroup);
							if (getVirtualTemplate($devices?.[vcDeviceID])) {
								showCreateTemplateModal = true;
							} else {
								showVCModalGroupSettings = true;
							}
						}}
					/>
				</div>
			{:else}
				{#if !device.shared}
					<Text
						class="link-styles text-small mt-s mb-s ml-xs"
						on:click={() => navigateToSetting(deviceID, 'virtual_components')}
					>
						{$format('text_virtual_component_open_main_device', {
							default: 'Open main device',
						})}
					</Text>
				{/if}
				{#if !group?.items?.length}
					<Alert
						type="info"
						heading={$format('no_virtual_components_title_new', {
							default: 'There are no {components} yet',
							values: {
								components: virtualBthomeTranslationValues(device.type),
							},
						})}
					/>
				{/if}
			{/if}
			{#each group?.items as item}
				{#if /^bthomebutton:/.test(item.key)}
					{#if $showNewLayout}
						<BthomeButtonControlV2 {device} component={item} />
					{:else}
						<BthomeButtonControl {device} component={item} />
					{/if}
				{:else if $showNewLayout}
					<VirtualComponentV2 device={_device} component={item} groupID={group.id} />
				{:else}
					<VirtualComponent device={_device} component={item} groupID={group.id} />
				{/if}
			{/each}
		</Container>
	{/each}
{/if}

{#if showCreateButton}
	<div>
		<Button
			class="mb-s"
			size="m"
			width="full"
			variant="secondary"
			on:click={() => {
				selectedGroup = null;
				showCreateTemplateModal = true;
			}}
		>
			{$format('vc_template_create_template', {
				default: 'Create Virtual Template Device',
			})}
		</Button>
		<Button size="m" width="full" on:click={() => (showVCModalCreate = true)}>
			{$format('virtual_components_create_group', {
				default: 'Create group',
			})}
		</Button>
	</div>
{/if}

{#if showCreateTemplateModal}
	<CreateTemplateModal
		device={_device}
		groupComponent={selectedGroup}
		bind:showMainModal={showVCModalGroupSettings}
		bind:showCreateTemplateModal
	/>
{:else if showVCModalGroupSettings}
	<VGroupModalSettings
		device={_device}
		component={selectedGroup}
		bind:showModal={showVCModalGroupSettings}
		bind:showCreateTemplateModal
	/>
{:else if showVCModalCreate}
	<CreateVCGroup {device} bind:showModal={showVCModalCreate} />
{/if}
